import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { BoardSettingsModel } from "models/BoardSettingsModel";
import { useApi } from "providers/Api";
import { useBoard } from "providers/Board";
import { useToasts } from "providers/Toasts";
import { useTranslations } from "providers/Translations";
import { PropsWithChildren, useCallback, useMemo } from "react";

import { BoardSettingsContext } from "./BoardSettingsContext";

export const BoardSettingsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { boardApi } = useApi();
  const { board } = useBoard();
  const i18n = useTranslations();
  const toasts = useToasts();

  const boardId = board?.id;
  const canEdit = board?.canEdit || false;

  const { isLoading, refetch, data, error } = useQuery({
    queryKey: ["boardsettings", boardId],
    queryFn: () => {
      if (!canEdit) throw new Error("User cannot edit board");
      return boardApi.getBoardSettings(boardId);
    },
    enabled: !!boardId && canEdit,
  });

  const reloadBoardSettings = useCallback(async () => {
    const result = await refetch();
    const newSettings = result?.data as BoardSettingsModel;
    if (newSettings) {
      toasts.refreshSuccess(i18n.Board, newSettings?.name);
    }
  }, [refetch, i18n, toasts]);

  const contextValue = useMemo(
    () => ({
      boardId,
      boardSettings: isLoading ? undefined : data,
      error: error as AxiosError | undefined,
      isLoading,
      reloadBoardSettings,
    }),
    [boardId, data, isLoading, error, reloadBoardSettings]
  );

  return <BoardSettingsContext.Provider value={contextValue}>{children}</BoardSettingsContext.Provider>;
};
